import {
  circle,
  ellipse,
  fill,
  flat,
  INK,
  line,
  P,
  poly,
  polyline,
  rect,
  shine,
  type IconDef,
  type Shape,
} from '../kit';

const r2 = (v: number): number => Math.round(v * 100) / 100;

/**
 * Redraws path data through a point map, for turning, mirroring or moving part of a drawing (the
 * kit has no transforms). `spin` (degrees), `flip` and `k` (scale) say what the map does to
 * shapes, so circular arcs follow along.
 */
function remap(
  d: string,
  f: (x: number, y: number) => readonly [number, number],
  spin = 0,
  flip = false,
  k = 1,
): string {
  const tok = d.match(/[a-zA-Z]|-?(?:\d+\.?\d*|\.\d+)/g) ?? [];
  let i = 0;
  const num = (): number => Number(tok[i++]);
  const pt = (px: number, py: number): string => {
    const [a, b] = f(px, py);
    return `${r2(a)} ${r2(b)}`;
  };
  let out = '';
  let cmd = 'M';
  let x = 0;
  let y = 0;
  let sx = 0;
  let sy = 0;
  while (i < tok.length) {
    const t = tok[i] as string;
    if (/[a-zA-Z]/.test(t)) {
      cmd = t;
      i++;
    } else if (cmd === 'Z' || cmd === 'z') {
      i++;
      continue;
    }
    const rel = cmd >= 'a';
    const ox = rel ? x : 0;
    const oy = rel ? y : 0;
    switch (cmd.toUpperCase()) {
      case 'Z':
        out += 'Z';
        x = sx;
        y = sy;
        break;
      case 'M':
        x = ox + num();
        y = oy + num();
        sx = x;
        sy = y;
        out += `M${pt(x, y)}`;
        cmd = rel ? 'l' : 'L';
        break;
      case 'L':
        x = ox + num();
        y = oy + num();
        out += `L${pt(x, y)}`;
        break;
      case 'H':
        x = ox + num();
        out += `L${pt(x, y)}`;
        break;
      case 'V':
        y = oy + num();
        out += `L${pt(x, y)}`;
        break;
      case 'C': {
        const ax = ox + num();
        const ay = oy + num();
        const bx = ox + num();
        const by = oy + num();
        x = ox + num();
        y = oy + num();
        out += `C${pt(ax, ay)} ${pt(bx, by)} ${pt(x, y)}`;
        break;
      }
      case 'Q': {
        const ax = ox + num();
        const ay = oy + num();
        x = ox + num();
        y = oy + num();
        out += `Q${pt(ax, ay)} ${pt(x, y)}`;
        break;
      }
      case 'A': {
        const rx = num() * k;
        const ry = num() * k;
        const rot = num();
        const large = num();
        const sweep = num();
        x = ox + num();
        y = oy + num();
        out += `A${r2(rx)} ${r2(ry)} ${r2(flip ? -rot : rot + spin)} ${large} ${flip ? 1 - sweep : sweep} ${pt(x, y)}`;
        break;
      }
      default:
        throw new Error(`remap: unsupported path command ${cmd}`);
    }
  }
  return out;
}

/** Turns path data `deg` degrees clockwise about (cx, cy). */
function turn(d: string, deg: number, cx = 16, cy = 16): string {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return remap(d, (x, y) => [cx + (x - cx) * c - (y - cy) * s, cy + (x - cx) * s + (y - cy) * c], deg);
}

/** Mirrors path data left to right about x = ax. */
const mirror = (d: string, ax = 16): string => remap(d, (x, y) => [2 * ax - x, y], 0, true);

/** Scales path data by k (about the origin), then moves it by (dx, dy). */
const shift = (d: string, dx: number, dy: number, k = 1): string =>
  remap(d, (x, y) => [x * k + dx, y * k + dy], 0, false, k);

/** Turns a whole drawing `deg` degrees clockwise about (cx, cy). */
const turned = (deg: number, cx: number, cy: number, shapes: Shape[]): Shape[] =>
  shapes.map((s) => ({ ...s, d: turn(s.d, deg, cx, cy) }));

/** Moves a whole drawing by (dx, dy). */
const moved = (dx: number, dy: number, shapes: Shape[]): Shape[] =>
  shapes.map((s) => ({ ...s, d: shift(s.d, dx, dy) }));

/** A circle wound the other way from `circle`: put it in the same path to punch a round hole. */
const hole = (cx: number, cy: number, r: number): string =>
  `M${r2(cx - r)} ${r2(cy)}a${r2(r)} ${r2(r)} 0 1 1 ${r2(2 * r)} 0a${r2(r)} ${r2(r)} 0 1 1 ${r2(-2 * r)} 0Z`;

/** A thick stroke with an ink edge down both sides (a wire, a strap, a hoop). */
const cord = (d: string, w: number, color: string, edge = 1.2): Shape[] => [
  line(d, w + 2 * edge),
  line(d, w, color),
];

/** A four-point sparkle. */
const sparkle = (x: number, y: number, r: number): string =>
  `M${x} ${r2(y - r)}Q${x} ${y} ${r2(x + r)} ${y}Q${x} ${y} ${x} ${r2(y + r)}Q${x} ${y} ${r2(x - r)} ${y}Q${x} ${y} ${x} ${r2(y - r)}Z`;

/** A zigzag from x0 to x1 in n legs, starting at y0 and bouncing to y1 and back. */
function zigzag(x0: number, x1: number, y0: number, y1: number, n: number): string {
  const pts: number[] = [];
  for (let j = 0; j <= n; j++) pts.push(x0 + ((x1 - x0) * j) / n, j % 2 ? y1 : y0);
  return polyline(...pts);
}

/** Criss-cross hatching inside a circle (a mic grille), lines `gap` apart. */
function mesh(cx: number, cy: number, r: number, gap: number): string {
  let d = '';
  for (const s of [1, -1])
    for (let p = -r + gap / 2; p < r; p += gap) {
      const h = Math.sqrt(r * r - p * p) * Math.SQRT1_2;
      const mx = cx - s * p * Math.SQRT1_2;
      const my = cy + p * Math.SQRT1_2;
      d += `M${r2(mx - h)} ${r2(my - s * h)}L${r2(mx + h)} ${r2(my + s * h)}`;
    }
  return d;
}

/** Short rays around (cx, cy) from radius r0 to r1, at these angles (degrees, clockwise from east). */
function rays(cx: number, cy: number, r0: number, r1: number, angles: readonly number[]): string {
  let d = '';
  for (const a of angles) {
    const c = Math.cos((a * Math.PI) / 180);
    const s = Math.sin((a * Math.PI) / 180);
    d += `M${r2(cx + r0 * c)} ${r2(cy + r0 * s)}L${r2(cx + r1 * c)} ${r2(cy + r1 * s)}`;
  }
  return d;
}

/** A cog with n teeth, wound clockwise so a `circle` inside it punches the axle hole. */
function cog(cx: number, cy: number, n: number, rIn: number, rOut: number): string {
  const pts: number[] = [];
  const w = Math.PI / n;
  for (let j = 0; j < n; j++) {
    const a = j * 2 * w - Math.PI / 2;
    for (const [da, r] of [
      [-0.5, rIn],
      [-0.3, rOut],
      [0.3, rOut],
      [0.5, rIn],
    ] as const)
      pts.push(cx + r * Math.cos(a + da * w), cy + r * Math.sin(a + da * w));
  }
  return poly(...pts);
}

/** Joined-up handwriting: loops of the given heights, each `u` wide, along the baseline y. */
function scrawl(x: number, y: number, heights: readonly number[], u = 2.2): string {
  let d = `M${r2(x - 0.6)} ${r2(y + 0.4)}`;
  let a = x;
  for (const h of heights) {
    d +=
      `C${r2(a + 0.9 * u)} ${r2(y - 0.25 * h)} ${r2(a + 1.05 * u)} ${r2(y - h)} ${r2(a + 0.55 * u)} ${r2(y - h)}` +
      `C${r2(a + 0.05 * u)} ${r2(y - h)} ${r2(a + 0.15 * u)} ${y} ${r2(a + u)} ${y}`;
    a += u;
  }
  return d;
}

/** Copper (the wire). */
const COPPER = '#d9793a';
/** Sun-bleached, smoke-stained siding. */
const GRIME = '#b7ae9a';
/** Neon tubing. */
const NEON_CYAN = '#5ff3ff';

/** A howling wolf, head and neck in profile, snout up and to the right; 11 wide, 16 tall. */
const WOLF =
  'M1 16C0.6 12 1.2 9 2.6 7L2.4 2.6L5 5.4C6.2 4.2 8 2.4 9.6 0.6C10.2 0 11 0.6 10.6 1.4L8 4.4H9.8C9.4 5.4 8.6 6.2 7.6 6.6' +
  'C7 8.4 7.2 10 8 11L7.2 11.4C7.6 13 8 14.6 8.4 16Z';

/** An ear of the good boy in the animals set (his left; mirror it for the right). */
const DOG_EAR = 'M6.4 8.6C4 12.6 4.4 18 7 19.8C9 18.2 9.6 13.4 10.8 10.4C9.8 9 8 8.4 6.4 8.6Z';

/** One half of a pair of scissors, lying flat: shank left of the pivot (16, 16), blade right. */
const SCISSOR_HALF = poly(9.2, 14.7, 15, 14.7, 17, 13.4, 28.6, 16.2, 15.4, 17.8, 9.2, 17.3);

/** A Christmas bulb hanging from (x, y), tipped `deg` degrees. */
const bulb = (x: number, y: number, deg: number, color: string): Shape[] =>
  turned(deg, x, y, [
    fill(P.darkGreen, rect(x - 1.5, y - 0.4, 3, 3, 0.5), { w: 1.1 }),
    fill(
      color,
      `M${x - 2.5} ${y + 4.6}C${x - 2.5} ${y + 2.6} ${x + 2.5} ${y + 2.6} ${x + 2.5} ${y + 4.6}C${x + 2.5} ${y + 7.2} ${x + 1} ${y + 9.2} ${x} ${y + 10.2}C${x - 1} ${y + 9.2} ${x - 2.5} ${y + 7.2} ${x - 2.5} ${y + 4.6}Z`,
      { w: 1.3 },
    ),
    shine(ellipse(x - 0.9, y + 5.2, 0.7, 1.3), 0.6),
  ]);

/** A feather hanging from (x, y), `h` long. */
const feather = (x: number, y: number, h: number, color: string, tip: string): Shape[] => [
  fill(
    color,
    `M${x} ${y}C${x + 2.2} ${y + 0.24 * h} ${x + 2.2} ${y + 0.68 * h} ${x} ${y + h}C${x - 2.2} ${y + 0.68 * h} ${x - 2.2} ${y + 0.24 * h} ${x} ${y}Z`,
    { w: 1.2 },
  ),
  flat(
    tip,
    `M${x - 1.25} ${y + 0.6 * h}C${x - 0.4} ${y + 0.64 * h} ${x + 0.4} ${y + 0.64 * h} ${x + 1.25} ${y + 0.6 * h}C${x + 1} ${y + 0.72 * h} ${x + 0.6} ${y + 0.8 * h} ${x} ${y + 0.87 * h}C${x - 0.6} ${y + 0.8 * h} ${x - 1} ${y + 0.72 * h} ${x - 1.25} ${y + 0.6 * h}Z`,
  ),
  line(`M${x} ${y + 1}V${r2(y + 0.88 * h)}`, 1, INK),
];

export const home = {
  /** A trailer's front door: a little window, a brass knob, a cinder-block step. */
  door: [
    fill(P.tan, rect(7, 3, 18, 26, 1.2)),
    fill(P.cream, rect(9.4, 5.2, 13.2, 23.8, 0.8)),
    fill(P.sky, rect(12, 8, 8, 6, 1)),
    fill(P.gold, circle(20, 18.4, 1.3), { w: 1.2 }),
    fill(P.gray, rect(6, 27.4, 20, 3, 0.8)),
  ],
  /**
   * Home: a single-wide, side on. A long, low cream box under a metal roof cap, a brown stripe,
   * little windows (one with an AC unit), a door up cinder-block steps, lattice skirting.
   */
  trailer: [
    fill(P.darkBrown, rect(4.6, 18.8, 22.8, 6.4, 0.6)),
    line(zigzag(5.6, 26.4, 21.2, 24.4, 12) + zigzag(5.6, 26.4, 24.4, 21.2, 12), 1.1, P.tan),
    fill(P.cream, rect(3, 7.6, 26, 12.6, 1.6)),
    fill(P.lightGray, rect(2.6, 6, 26.8, 2.8, 1.4)),
    flat(P.wood, rect(3.8, 15, 24.4, 2.2)),
    fill(P.sky, rect(5.4, 10.2, 4.8, 4, 0.5) + rect(12, 10.2, 4.8, 4, 0.5) + rect(25, 10.2, 2.6, 4, 0.5), {
      w: 1.3,
    }),
    fill(P.lightGray, rect(5, 12.2, 5.6, 3.4, 0.5), { w: 1.2 }),
    line('M6.2 13.4H9.4M6.2 14.6H9.4', 0.9, P.gray),
    fill(P.tan, rect(19.2, 9.8, 4.4, 10.4, 0.5), { w: 1.3 }),
    flat(P.sky, rect(20.3, 11, 2.2, 2.4, 0.3)),
    fill(P.gray, rect(19, 20.2, 4.8, 2.8, 0.3), { w: 1.2 }),
    fill(P.gray, rect(17, 23, 8.8, 2.8, 0.3), { w: 1.2 }),
    line('M21.4 23.2V25.6', 1.1),
  ],
  /**
   * The dream: a double-wide. Wider and grander than a single-wide, with a real pitched roof,
   * two-tone white and blue siding, flower boxes, a red door up a railed porch, and a sparkle.
   */
  'double-wide': [
    fill(P.gold, sparkle(5, 4.8, 2.6) + sparkle(27.2, 3.8, 1.5), { w: 1 }),
    fill(P.navy, poly(2.6, 14, 7.4, 8.4, 24.6, 8.4, 29.4, 14)),
    fill(P.white, rect(3.4, 13.4, 25.2, 11.6, 0.6)),
    flat(P.blue, rect(4.2, 19.8, 23.6, 4.4)),
    fill(
      P.sky,
      rect(5.2, 14.8, 4.8, 4, 0.5) + rect(11.4, 14.8, 4.8, 4, 0.5) + rect(24.6, 14.8, 2.6, 4, 0.5),
      {
        w: 1.3,
      },
    ),
    fill(P.wood, rect(4.8, 18.6, 5.6, 2.2, 0.5) + rect(11, 18.6, 5.6, 2.2, 0.5), { w: 1.2 }),
    flat(P.red, circle(6.4, 18.2, 1) + circle(9, 18.2, 1) + circle(12.6, 18.2, 1) + circle(15.2, 18.2, 1)),
    flat(P.yellow, circle(7.7, 17.8, 1) + circle(13.9, 17.8, 1)),
    fill(P.red, rect(18.8, 14.4, 4.4, 10.2, 0.5), { w: 1.3 }),
    fill(P.wood, rect(15.8, 23.8, 11.2, 2.4, 0.4), { w: 1.3 }),
    fill(P.wood, rect(18.2, 26, 5.6, 2.2, 0.3), { w: 1.2 }),
    line('M16.6 23.8V21.2M24.6 23.8V21.2M26.2 23.8V21.2', 1.1),
    fill(P.white, rect(15.8, 20.4, 2.2, 1.4, 0.5) + rect(24, 20.4, 3, 1.4, 0.5), { w: 1.1 }),
  ],
  /**
   * The burnt-out wreck on Lot 10: a single-wide gone wrong. Listing, smoke-stained, a hole in the
   * roof, a boarded-up window, no door, skirting gone, propped on one block, weeds.
   */
  shack: moved(0, -1.4, [
    fill(P.gray, rect(22, 21, 5.6, 4.6, 0.3), { w: 1.2 }),
    ...turned(-6, 16, 18, [
      fill(
        GRIME,
        'M3.4 11.4C3.4 10.5 4.1 9.8 5 9.8H9.4L11 12.6L12.6 10.8L14.4 13.2L16.2 9.8H27C27.9 9.8 28.6 10.5 28.6 11.4V21.8C28.6 22.7 27.9 23.4 27 23.4H5C4.1 23.4 3.4 22.7 3.4 21.8Z',
      ),
      flat(P.rust, rect(4.2, 17.6, 23.6, 2)),
      flat(
        P.charcoal,
        'M4.2 11.2C5.6 10.6 7.6 10.6 9.4 10.6L11 13.4L14.4 14L16.8 10.6C17.4 12.8 16.2 15 13.8 15.8C11 16.6 7.6 15.6 4.2 16.6Z',
        0.55,
      ),
      fill(P.black, rect(5.8, 12.6, 4.8, 4, 0.4), { w: 1.3 }),
      fill(P.wood, turn(rect(4.6, 13.7, 7.2, 1.8, 0.3), 18, 8.2, 14.6), { w: 1.1 }),
      fill(P.wood, turn(rect(4.6, 13.7, 7.2, 1.8, 0.3), -18, 8.2, 14.6), { w: 1.1 }),
      fill(P.black, rect(19.4, 12, 4.4, 11.4, 0.4), { w: 1.3 }),
      fill(P.black, rect(25, 12.6, 2.2, 4, 0.4), { w: 1.3 }),
    ]),
    fill(
      P.grass,
      'M2.8 27.4C3 25.2 4 23.8 5.2 23.4L5.8 25L6.8 22.6L7.8 24.8L9.2 23L9.6 25.2C10.8 25.4 11.4 26.4 11.6 27.4Z',
      { w: 1.2 },
    ),
    fill(
      P.grass,
      'M16.4 27.4C16.6 26 17.4 25 18.2 24.8L18.8 25.8L19.8 24.4L20.4 25.6C21.2 25.8 21.6 26.6 21.8 27.4Z',
      {
        w: 1.2,
      },
    ),
  ]),
  /** The porch couch: orange-and-brown plaid, front on. */
  couch: [
    fill(P.orange, rect(5.4, 7.6, 21.2, 10.8, 3)),
    fill(P.orange, rect(6.4, 15.6, 19.2, 5.8, 1.6)),
    fill(P.orange, rect(5.6, 20.4, 20.8, 3.6, 0.8)),
    line('M16 16.4V20.6', 1.3),
    fill(P.orange, rect(2.6, 12, 6, 12, 2.6) + rect(23.4, 12, 6, 12, 2.6)),
    flat(
      P.brown,
      rect(10, 8.4, 2.2, 6.4) +
        rect(15, 8.4, 2.2, 6.4) +
        rect(19.8, 8.4, 2.2, 6.4) +
        rect(10, 16.4, 2.2, 4.2) +
        rect(19.8, 16.4, 2.2, 4.2) +
        rect(10, 21.2, 2.2, 2) +
        rect(19.8, 21.2, 2.2, 2) +
        rect(4.6, 12.8, 2, 10.4) +
        rect(25.4, 12.8, 2, 10.4),
      0.55,
    ),
    flat(
      P.brown,
      rect(9.4, 10.8, 13.2, 2) +
        rect(9.4, 17.8, 13.2, 2) +
        rect(3.4, 17.8, 4.4, 2) +
        rect(24.2, 17.8, 4.4, 2),
      0.55,
    ),
    fill(P.darkBrown, rect(4.2, 23.8, 2.6, 2.6, 0.6) + rect(25.2, 23.8, 2.6, 2.6, 0.6), { w: 1.2 }),
  ],
  /** A brown recliner, side on, footrest kicked up. */
  recliner: [
    line('M19.6 22.6L24.4 20M19.8 20.2L24.6 22.4', 1.3),
    fill(P.wood, turn(rect(6.8, 4.6, 7.4, 17, 3.4), -14, 9.5, 21)),
    line(turn('M8.2 10.4H12.8M8.2 15H12.8', -14, 9.5, 21), 1.2, P.brown),
    fill(P.wood, turn(rect(20.6, 15.6, 8.8, 4, 2), -8, 20.6, 17.6)),
    fill(P.brown, rect(7.4, 12.6, 14.8, 11.8, 3)),
    line('M10 16.2H19.6', 1.2, P.darkBrown),
    fill(P.darkBrown, rect(8.8, 24.2, 2.6, 2.6, 0.5) + rect(17.8, 24.2, 2.6, 2.6, 0.5), { w: 1.2 }),
  ],
  /** A bed, side on: tall headboard, a pillow, a patchwork quilt. */
  bed: moved(0, -0.8, [
    fill(P.wood, rect(3, 6.4, 5.8, 21.4, 2.2)),
    fill(P.wood, rect(25.8, 14, 3.6, 13.8, 1.4)),
    fill(P.wood, rect(7.6, 21.2, 19, 3.2, 0.4)),
    fill(P.white, rect(8, 16, 18.6, 5.6, 1.2)),
    fill(P.white, rect(8.6, 12, 7.6, 4.8, 2.4)),
    fill(
      P.red,
      'M12.4 16.2C12.4 14.6 13.6 13.6 15.2 13.6H26.2V22.6C24.8 23.8 23.2 23.8 21.8 22.8C20.4 23.8 18.8 23.8 17.4 22.8C16 23.8 14.2 23.6 13 22.6C12.6 22.2 12.4 21.6 12.4 21Z',
    ),
    flat(
      P.pink,
      rect(13.2, 14.4, 2.8, 2.8) +
        rect(18.8, 14.4, 2.8, 2.8) +
        rect(16, 17.2, 2.8, 2.8) +
        rect(21.6, 17.2, 2.8, 2.8) +
        rect(13.2, 20, 2.8, 1.6) +
        rect(18.8, 20, 2.8, 1.6),
    ),
    flat(
      P.gold,
      circle(14.6, 15.8, 0.8) + circle(20.2, 15.8, 0.8) + circle(17.4, 18.6, 0.8) + circle(23, 18.6, 0.8),
    ),
  ]),
  /** A waterbed: a boxy wooden frame holding a wavy blue water mattress. */
  waterbed: moved(0, -0.8, [
    fill(P.wood, rect(3, 7.6, 5.4, 12.4, 1.4)),
    fill(
      P.blue,
      'M5.6 19.4C5.4 15.6 6.8 12.2 9.4 12.2C11.6 12.2 12.4 13.8 14.4 13.8C16.4 13.8 17.2 12 19.4 12C21.6 12 22.4 13.8 24.4 13.8C26 13.8 26.8 12.6 27.6 12.6C28.4 14.6 28.2 17.2 28 19.4Z',
    ),
    line('M9.4 16.4C10.8 15.6 12.2 17.2 13.6 16.4M17.4 16.2C18.8 15.4 20.2 17 21.6 16.2', 1.2, P.white, 0.8),
    fill(P.white, rect(5.6, 9.4, 6.4, 3.8, 1.9)),
    fill(P.wood, rect(3, 18, 26, 6.6, 1)),
    line('M4.6 21.4H27.4', 1.2, P.brown),
    fill(P.darkBrown, rect(5, 24.2, 22, 2.4, 0.4), { w: 1.2 }),
  ]),
  /** A busted office chair: torn seat, foam coming out, one caster missing so it lists. */
  chair: [
    ...turned(5, 7, 27, [
      fill(P.darkGray, 'M5.4 24H26.6L26.2 25.8H5.8Z', { w: 1.3 }),
      fill(P.gray, rect(14.4, 17.6, 3.2, 6.8, 0.6)),
      fill(P.gray, rect(6.8, 10, 2.4, 7.4, 0.8)),
      fill(P.charcoal, rect(4.4, 2.8, 7, 10.6, 2.8)),
      fill(P.charcoal, rect(6.4, 14.4, 18.6, 4.4, 2.2)),
      fill(
        P.lemon,
        'M12 14.8C11.4 13 12.8 11.8 14.2 12.6C15 11.4 17.2 11.6 17.4 13.2C19 13.2 19.4 14.8 18.4 15.4L15.4 16.4Z',
        { w: 1.2 },
      ),
      fill(P.black, circle(7, 27, 1.8) + circle(16, 27, 1.8)),
    ]),
  ],
  /** A white toilet, side on. */
  toilet: [
    fill(P.white, rect(4.6, 5.2, 9, 11.6, 1.6)),
    fill(P.white, rect(3.8, 3.4, 10.6, 2.8, 1.2)),
    fill(P.silver, rect(11.2, 7.4, 4, 1.6, 0.8), { w: 1.1 }),
    fill(
      P.white,
      'M7.4 15.8H27.4C27.4 20.8 24.2 23.8 20 24L20.6 28.2H10.8L11.6 23C8.8 21.8 7.4 19.4 7.4 15.8Z',
    ),
    flat(P.silver, 'M12.4 23.8C14.6 24.4 17 24.4 19.2 24L19.6 27.4H12Z'),
    fill(P.white, rect(6.8, 14.2, 21.2, 2.6, 1.3)),
  ],
  /** A white clawfoot tub with gold feet, full of bubbles. */
  'clawfoot-tub': [
    fill(
      P.gold,
      'M6.6 20.4C7.4 22.6 7 24.6 5.4 26.4C6.8 27.4 8.8 27.2 9.8 26.4C9.6 24.6 9.8 22.8 10.8 21.6Z' +
        mirror('M6.6 20.4C7.4 22.6 7 24.6 5.4 26.4C6.8 27.4 8.8 27.2 9.8 26.4C9.6 24.6 9.8 22.8 10.8 21.6Z'),
      { w: 1.3 },
    ),
    fill(P.white, 'M3.6 13.4H28.4C28.4 19.8 25.4 24 19.4 24H12.6C6.6 24 3.6 19.8 3.6 13.4Z'),
    fill(P.white, ellipse(16, 12.8, 13.4, 3)),
    flat(P.water, ellipse(16, 12.8, 11.6, 1.8)),
    fill(P.white, circle(10.4, 10.8, 2.6), { w: 1.2 }),
    fill(P.white, circle(15.2, 9.6, 3.2), { w: 1.2 }),
    fill(P.white, circle(20.2, 10.8, 2.4), { w: 1.2 }),
    fill(P.white, circle(24.4, 6, 1.5), { w: 1.1 }),
    shine(ellipse(8, 16.6, 1.2, 2.2), 0.5),
  ],
  /** An old CRT TV: wood-grain case, rabbit ears, glowing blue screen. */
  tv: [
    line('M14.6 8L9.4 3.8M17.4 8L23 4.2', 1.4),
    fill(P.silver, circle(9.4, 3.8, 1.1) + circle(23, 4.2, 1.1), { w: 1.1 }),
    fill(P.charcoal, ellipse(16, 8.6, 3.4, 1.8), { w: 1.3 }),
    fill(P.wood, rect(3, 8.6, 26, 17.4, 2.6)),
    fill(P.sky, rect(5.4, 11, 16, 12.6, 3.6)),
    shine(ellipse(9.4, 14.2, 1.6, 1), 0.7),
    fill(P.cream, circle(25.2, 13.6, 1.7) + circle(25.2, 18.2, 1.7), { w: 1.2 }),
    line('M23.8 21.8H26.6M23.8 23.4H26.6', 1.1),
    fill(P.darkBrown, rect(6, 25.6, 2.6, 2.6, 0.4) + rect(23.4, 25.6, 2.6, 2.6, 0.4), { w: 1.2 }),
  ],
  /** A huge black flat-screen on a stand, picture bright. */
  'big-tv': [
    fill(P.charcoal, 'M13.4 21.6H18.6L19.6 25.6H12.4Z'),
    fill(P.charcoal, rect(8.6, 25, 14.8, 2.4, 1.2)),
    fill(P.black, rect(2.8, 5.4, 26.4, 17, 1.2)),
    flat(P.sky, rect(4.2, 6.8, 23.6, 14.2, 0.4)),
    flat(P.grass, 'M4.2 15.4C9 13.6 14 13.4 19 14.2C22.4 14.8 25.4 14.8 27.8 14V21H4.2Z'),
    shine(poly(6.2, 6.8, 11, 6.8, 6.2, 12), 0.5),
  ],
  /** A ham radio: a boxy set with dials, a hand mic on a coiled cord, an antenna. */
  'ham-radio': moved(0, 1.2, [
    line('M7.4 11V4', 1.6),
    fill(P.red, circle(7.4, 3.8, 1.3), { w: 1.1 }),
    line('M3.8 3.2C2.8 4.4 2.8 6 3.8 7.2M11 3.2C12 4.4 12 6 11 7.2', 1.2),
    fill(P.charcoal, rect(3, 11, 20, 13.6, 1.6)),
    fill(P.lime, rect(5.2, 13.4, 9.4, 4, 0.6), { w: 1.2 }),
    line('M6.6 15.4H8M9.2 15.4H10.6M11.8 15.4H13.2', 1.2, P.darkGreen),
    fill(P.silver, circle(18.4, 17.4, 3.2), { w: 1.3 }),
    line('M18.4 17.4L19.8 15.4', 1.2),
    fill(P.silver, circle(6.8, 21.2, 1.4) + circle(10.8, 21.2, 1.4), { w: 1.1 }),
    line('M26 20.6C26 22.4 24.6 22.2 24.6 23.4C24.6 24.6 26 24.4 26 25.6C26 26.8 24.4 26.8 23 25.6', 1.2),
    fill(P.charcoal, rect(23.6, 12.4, 5, 8.6, 2.4)),
    line('M25.2 14.8H27M25.2 16.6H27', 1.1, P.gray),
  ]),
  /** A karaoke microphone (silver head, black handle) with music notes. */
  microphone: moved(1, 0.6, [
    ...turned(30, 17.4, 16, [
      fill(P.black, 'M14.2 14.6H20.6L19.2 27.6C19.1 28.6 15.7 28.6 15.6 27.6Z'),
      flat(P.hotPink, rect(15, 17.2, 4.8, 1.8)),
      fill(P.darkGray, rect(13.8, 12.4, 7.2, 2.8, 1)),
      fill(P.silver, circle(17.4, 7.8, 5.4)),
      line(mesh(17.4, 7.8, 4.2, 2.1), 0.9, P.gray),
      shine(ellipse(15, 5.4, 1.4, 1), 0.7),
    ]),
    ...cord('M5.6 12.2V5.2L11 3.6V10.6', 1.2, P.hotPink, 0.9),
    fill(P.hotPink, turn(ellipse(4.4, 12.4, 1.9, 1.5), -20, 4.4, 12.4), { w: 1.2 }),
    fill(P.hotPink, turn(ellipse(9.8, 10.8, 1.9, 1.5), -20, 9.8, 10.8), { w: 1.2 }),
  ]),
  /** An old gray game controller. */
  gamepad: moved(0, 1.4, [
    line('M16 10.6C16 8 18.4 7.6 18.4 5.2', 1.6),
    fill(
      P.lightGray,
      'M9.6 9.6C12 9.6 13.4 10.6 16 10.6C18.6 10.6 20 9.6 22.4 9.6C26.4 9.6 29 13 29 16.8C29 20.8 26.4 24 22.4 24C20 24 18.6 23 16 23C13.4 23 12 24 9.6 24C5.6 24 3 20.8 3 16.8C3 13 5.6 9.6 9.6 9.6Z',
    ),
    fill(P.charcoal, 'M8.4 13.2H10.8V15.6H13.2V18H10.8V20.4H8.4V18H6V15.6H8.4Z', { w: 1.1 }),
    fill(P.blue, circle(22.4, 13.8, 1.4), { w: 1 }),
    fill(P.red, circle(25.4, 16.8, 1.4), { w: 1 }),
    fill(P.yellow, circle(22.4, 19.8, 1.4), { w: 1 }),
    fill(P.green, circle(19.4, 16.8, 1.4), { w: 1 }),
  ]),
  /** A lava lamp: silver base and cap, purple goo with orange blobs. */
  'lava-lamp': [
    fill(P.purple, 'M12.2 20.6C11.4 16.6 12 11.6 13.8 7.2H18.2C20 11.6 20.6 16.6 19.8 20.6Z'),
    flat(P.orange, ellipse(15.4, 14.6, 2.2, 2.8)),
    flat(P.orange, ellipse(17, 9.8, 1.2, 1.5)),
    flat(P.orange, 'M13 20.4C13 18.6 14.2 17.8 15.4 18.4C16.6 17.4 18.8 17.8 19 20.4Z'),
    shine('M13.2 18C12.8 15 13.2 12 14.2 9.4L14.8 9.6C14 12.2 13.8 15 14.2 18Z', 0.45),
    fill(P.silver, 'M13.4 7.6L14.4 3.6H17.6L18.6 7.6Z'),
    fill(P.silver, 'M12 20H20L23.2 28.6H8.8Z'),
  ],
  /** A tanning bed: a clamshell propped open, glowing purple-blue tubes inside. */
  'tanning-bed': [
    flat(P.lavender, poly(5.6, 18.6, 27.4, 10, 29, 18.6), 0.9),
    line('M9.4 17.8L24.6 12.4M9.4 18.6L26.4 15.8', 1.1, P.white, 0.9),
    fill(P.charcoal, rect(6, 23.4, 20.8, 3.6, 1)),
    fill(P.white, rect(3.8, 18.4, 25.4, 5.6, 2.4)),
    flat(P.purple, rect(5.4, 19.2, 22.2, 1.8, 0.9)),
    ...turned(-22, 5.6, 17.6, [
      fill(P.white, rect(4.4, 13.4, 25, 5, 2.4)),
      flat(P.purple, rect(6, 16.2, 21.8, 1.8, 0.9)),
    ]),
  ],
  /** An old white garage fridge with a COLD BEER sticker and a beer-can magnet. */
  'beer-fridge': [
    fill(P.white, 'M7.6 6.4C7.6 4.2 9.2 2.8 11.6 2.8H20.4C22.8 2.8 24.4 4.2 24.4 6.4V27.4H7.6Z'),
    flat(P.silver, rect(21.8, 5.4, 1.8, 21.2, 0.6)),
    line('M8.4 10.6H23.6', 1.3),
    fill(P.silver, rect(19.6, 5.2, 1.8, 3.6, 0.9) + rect(19.6, 12.6, 1.8, 6.4, 0.9), { w: 1.1 }),
    fill(P.red, rect(9.4, 13, 8.8, 6.4, 1), { w: 1.2 }),
    line('M10.8 15.4Q11.6 14.2 12.4 15.4T14 15.4T15.6 15.4T17.2 15.4M10.8 17.6H16.8', 1.1, P.white),
    fill(P.gold, rect(11.6, 21.2, 3.6, 4.6, 0.8), { w: 1.2 }),
    flat(P.blue, rect(12.4, 22.8, 2, 1.6)),
    fill(P.charcoal, rect(8.6, 27, 2.6, 2, 0.4) + rect(20.8, 27, 2.6, 2, 0.4), { w: 1.1 }),
  ],
  /** A neon COLD BEER sign: glowing pink and cyan tubes on a dark backing. */
  'neon-beer': moved(0, 1, [
    line(polyline(7, 8, 16, 3.4, 25, 8), 1.2),
    fill(P.charcoal, rect(2.8, 7.6, 26.4, 18.4, 3)),
    line(scrawl(6.6, 15, [3.6, 2, 2, 3.6, 2], 3.6), 3.4, P.hotPink, 0.35),
    line(scrawl(6.6, 15, [3.6, 2, 2, 3.6, 2], 3.6), 1.4, P.pink),
    line(
      'M6.6 22.4V18.6H9.6V22.4M11.8 22.4V18.6H14.8V22.4M17 22.4V18.6H20V22.4M22.2 22.4V18.6H25.2',
      3.4,
      NEON_CYAN,
      0.35,
    ),
    line(
      'M6.6 22.4V18.6H9.6V22.4M11.8 22.4V18.6H14.8V22.4M17 22.4V18.6H20V22.4M22.2 22.4V18.6H25.2',
      1.4,
      NEON_CYAN,
    ),
  ]),
  /** A dream catcher: a hoop strung with a web, beads, feathers hanging below. */
  'dream-catcher': [
    line('M10.4 18.4L9.2 21.6M16 20V22.6M21.6 18.4L22.8 21.6', 1.1),
    ...feather(9, 21.6, 7, P.cream, P.brown),
    ...feather(16, 22.4, 6.8, P.cream, P.teal),
    ...feather(23, 21.6, 7, P.cream, P.brown),
    line(
      polyline(
        ...Array.from({ length: 17 }, (_, j) => {
          const a = (j * Math.PI) / 8 - Math.PI / 2;
          const r = j % 2 ? 3.4 : 7.2;
          return [16 + r * Math.cos(a), 11.6 + r * Math.sin(a)];
        }).flat(),
      ),
      1,
      P.tan,
    ),
    ...cord(circle(16, 11.6, 7.6), 2, P.wood),
    fill(P.teal, circle(16, 11.6, 1.6), { w: 1.1 }),
    fill(P.red, circle(9.8, 20, 1.1) + circle(22.2, 20, 1.1), { w: 1 }),
  ],
  /** A gold-framed velvet painting of dogs playing poker: a dog and his cards on dark green. */
  'dogs-poker': [
    fill(P.gold, rect(2.6, 4, 26.8, 24.4, 1.4)),
    fill(P.darkGreen, rect(5.4, 6.8, 21.2, 18.8, 0.4), { w: 1.2 }),
    fill(P.tan, shift(ellipse(16, 16.4, 9.6, 9), 4.6, 6.6, 0.5), { w: 1.1 }),
    fill(P.brown, shift(DOG_EAR + mirror(DOG_EAR), 4.6, 6.6, 0.5), { w: 1.1 }),
    fill(P.cream, shift(ellipse(16, 21.2, 5.6, 4.2), 4.6, 6.6, 0.5), { w: 1.1 }),
    flat(
      P.black,
      shift(ellipse(16, 18.8, 2.3, 1.6) + circle(12, 14.8, 1.4) + circle(20, 14.8, 1.4), 4.6, 6.6, 0.5),
    ),
    line('M13.8 18.4L17.4 19.6', 1.6, P.brown),
    fill(P.white, turn(rect(17.2, 16.6, 4.6, 6.4, 0.6), -14, 19.5, 19.8), { w: 1.2 }),
    flat(P.red, turn(poly(19.5, 18, 20.6, 19.8, 19.5, 21.6, 18.4, 19.8), -14, 19.5, 19.8)),
    fill(P.white, turn(rect(20, 16.6, 4.6, 6.4, 0.6), 10, 22.3, 19.8), { w: 1.2 }),
    flat(P.red, turn(poly(22.3, 18, 23.4, 19.8, 22.3, 21.6, 21.2, 19.8), 10, 22.3, 19.8)),
  ],
  /** A wall tapestry: three wolves howling at a big full moon, on dark blue. */
  'wolf-tapestry': [
    fill(P.navy, rect(4.6, 4.4, 22.8, 21.8)),
    line(
      'M6 26.6V28.4M8.4 26.6V28.4M10.8 26.6V28.4M13.2 26.6V28.4M15.6 26.6V28.4M18 26.6V28.4M20.4 26.6V28.4M22.8 26.6V28.4M25.2 26.6V28.4',
      1.2,
      P.gold,
    ),
    flat(P.lemon, circle(16, 11.8, 6.4)),
    flat(P.beige, circle(13.8, 9.8, 1.2) + circle(18.6, 13.2, 1), 0.8),
    flat(P.white, circle(7.4, 8, 0.8) + circle(25, 7.2, 0.8) + circle(24.2, 15.4, 0.7), 0.9),
    flat(P.gray, mirror(shift(WOLF, 4.4, 13.9, 0.72), 9.4) + shift(WOLF, 18.6, 13.9, 0.72)),
    flat(P.silver, shift(WOLF, 11, 11.3, 0.88)),
    fill(P.wood, rect(2.8, 3.2, 26.4, 2.4, 1.2)),
  ],
  /** A lit white candle with a drip of wax. */
  candle: [
    flat(P.yellow, circle(16, 7.4, 5.4), 0.3),
    fill(P.gold, ellipse(16, 26.4, 8.4, 2.4)),
    fill(P.cream, rect(11.4, 12.4, 9.2, 14, 1)),
    fill(
      P.white,
      'M11.4 13.4C11.4 12.8 11.8 12.4 12.4 12.4H19.6C20.2 12.4 20.6 12.8 20.6 13.4V15C20.6 15.8 19.4 15.8 19.2 15V18.4C19.2 19.4 17.6 19.4 17.6 18.4V15.2C16.2 15.8 14.4 15.6 13.2 15.2C12.8 16.4 11.4 16.4 11.4 15Z',
      { w: 1.2 },
    ),
    line('M16 12.4V10.2', 1.2),
    fill(
      P.fire,
      'M16 2.6C17.8 5 19 6.6 19 8.4C19 10.2 17.6 11.2 16 11.2C14.4 11.2 13 10.2 13 8.4C13 6.6 14.2 5 16 2.6Z',
    ),
    flat(
      P.flame,
      'M16 6C16.9 7.2 17.4 8 17.4 8.9C17.4 9.7 16.8 10.2 16 10.2C15.2 10.2 14.6 9.7 14.6 8.9C14.6 8 15.1 7.2 16 6Z',
    ),
  ],
  /** A glowing yellow light bulb. */
  bulb: [
    line(rays(16, 12.4, 10.4, 12.6, [180, 0, 225, 315]), 1.6, P.amber),
    fill(
      P.yellow,
      'M16 4.4C11.4 4.4 8.2 7.8 8.2 12.2C8.2 15.2 9.8 16.8 11.2 18.6C12 19.6 12.2 20.6 12.2 21.6H19.8C19.8 20.6 20 19.6 20.8 18.6C22.2 16.8 23.8 15.2 23.8 12.2C23.8 7.8 20.6 4.4 16 4.4Z',
    ),
    line('M14 18.6V15.4C14 13.2 18 13.2 18 15.4V18.6', 1.2, P.orange),
    shine(ellipse(12, 10.2, 1.6, 2.6), 0.6),
    fill(P.silver, rect(12, 21.4, 8, 5, 1)),
    line('M13.4 23.2H18.6M13.4 24.8H18.6', 1.1, P.gray),
    fill(P.charcoal, 'M13.8 26.4H18.2L17.2 28.6H14.8Z', { w: 1.2 }),
  ],
  /** A wooden grandfather clock: round face, pendulum behind glass. */
  'grandfather-clock': [
    fill(P.wood, 'M8.6 15.8V8C8.6 5.6 12 4.2 16 4.2C20 4.2 23.4 5.6 23.4 8V15.8Z'),
    fill(P.gold, circle(16, 3.4, 1), { w: 1.1 }),
    fill(P.cream, circle(16, 10.2, 4.2), { w: 1.3 }),
    line('M16 10.2V7.6M16 10.2L18 11.4', 1.2),
    fill(P.wood, rect(10.6, 15.4, 10.8, 9.6)),
    fill(P.darkBrown, rect(12.8, 16.6, 6.4, 7.2, 1), { w: 1.2 }),
    line('M16 16.8V20.4', 1.1, P.gold),
    fill(P.gold, circle(16, 21, 1.6), { w: 1.1 }),
    fill(P.wood, rect(9, 24.6, 14, 4.4, 0.8)),
  ],
  /** A red twin-bell alarm clock. */
  'alarm-clock': [
    line('M10.8 24.6L8.4 28.4M21.2 24.6L23.6 28.4', 2.2),
    fill(
      P.red,
      turn('M5.2 9.6A4.2 4.2 0 0 1 13.6 9.6Z', -40, 9.4, 9.6) +
        turn('M18.4 9.6A4.2 4.2 0 0 1 26.8 9.6Z', 40, 22.6, 9.6),
    ),
    line('M16 7.6V4.6', 1.6),
    fill(P.red, circle(16, 17.4, 10)),
    fill(P.white, circle(16, 17.4, 7.4), { w: 1.2 }),
    line('M16 17.4V12.8M16 17.4L19.4 19.2', 1.5),
    flat(INK, circle(16, 17.4, 1)),
    shine(ellipse(10.2, 11.6, 1.6, 1), 0.6),
  ],
  /** A silver stopwatch. */
  stopwatch: [
    fill(P.silver, rect(14.6, 5, 2.8, 3.4, 0.4), { w: 1.2 }),
    fill(P.silver, rect(12.8, 2.6, 6.4, 3, 1.2)),
    fill(P.silver, turn(rect(23, 7.6, 2.8, 3.6, 0.6), 45, 24.4, 9.4), { w: 1.2 }),
    fill(P.silver, circle(16, 18, 10.2)),
    fill(P.white, circle(16, 18, 7.8), { w: 1.2 }),
    line('M16 11.4V12.8M22.6 18H21.2M16 24.6V23.2M9.4 18H10.8', 1.2),
    line('M16 18L19.4 13.6', 1.5, P.red),
    flat(INK, circle(16, 18, 1)),
  ],
  /** A plastic laundry basket heaped with clothes. */
  laundry: moved(0, -1.4, [
    fill(P.red, 'M5.6 15.6C5 12.4 6.8 10 9.6 10.4C10.6 8.6 13.2 8.4 14.6 9.8L15.4 15.6Z'),
    fill(P.white, 'M12.6 15.6C12 11.6 13.8 8 17.4 7.6C21 7.4 22.6 10.4 22.2 15.6Z'),
    fill(P.green, 'M19.6 15.6C19.4 12.2 21.6 10.4 24 11C26.4 11.6 27.2 13.6 26.6 15.6Z'),
    fill(P.blue, 'M4.4 17H27.6L25.4 27.6C25.2 28.4 24.6 28.8 23.8 28.8H8.2C7.4 28.8 6.8 28.4 6.6 27.6Z'),
    fill(P.blue, rect(3, 14.4, 26, 3, 1.4)),
    flat(
      P.navy,
      rect(8.6, 19.6, 3.4, 1.8, 0.9) +
        rect(14.3, 19.6, 3.4, 1.8, 0.9) +
        rect(20, 19.6, 3.4, 1.8, 0.9) +
        rect(9.2, 23.4, 3.2, 1.8, 0.9) +
        rect(14.4, 23.4, 3.2, 1.8, 0.9) +
        rect(19.6, 23.4, 3.2, 1.8, 0.9),
      0.55,
    ),
    fill(P.red, 'M6.4 15.6L4.8 21.4L7.4 22L8.8 16.2Z', { w: 1.3 }),
  ]),
  /** A yellow car-wash sponge with soap bubbles. */
  sponge: [
    fill(
      P.yellow,
      'M5 12.6C5 10.2 7 9 9 9.6C11.4 10.4 13.6 11 16 11C18.4 11 20.6 10.4 23 9.6C25 9 27 10.2 27 12.6V21.4C27 23.8 25 25 23 24.4C20.6 23.6 18.4 23 16 23C13.6 23 11.4 23.6 9 24.4C7 25 5 23.8 5 21.4Z',
    ),
    flat(
      P.amber,
      circle(9, 14.4, 0.9) +
        circle(13, 16.8, 0.9) +
        circle(18.4, 15, 0.9) +
        circle(22.6, 18.6, 0.9) +
        circle(10.4, 20, 0.9) +
        circle(17, 20.4, 0.9) +
        circle(23.4, 13.4, 0.9),
    ),
    fill(P.white, circle(8.4, 7.4, 2.4), { w: 1.2 }),
    fill(P.white, circle(12.4, 5.4, 1.6), { w: 1.1 }),
    fill(P.white, circle(25, 26.6, 1.8), { w: 1.1 }),
    shine(circle(7.6, 6.6, 0.7), 0.8),
  ],
  /** A little amber essential-oil bottle: black dropper cap, a lavender sprig on the label. */
  'oil-bottle': [
    fill(P.black, 'M14 9V6C14 4.2 14.8 3 16 3C17.2 3 18 4.2 18 6V9Z'),
    fill(P.black, rect(12.2, 8.4, 7.6, 4.4, 0.8)),
    fill(
      P.rust,
      'M10.4 15C10.4 13.4 11.6 12.4 13 12.4H19C20.4 12.4 21.6 13.4 21.6 15V27C21.6 28 20.8 28.8 19.8 28.8H12.2C11.2 28.8 10.4 28 10.4 27Z',
    ),
    fill(P.cream, rect(11.8, 17, 8.4, 8.6, 0.6), { w: 1.1 }),
    line('M16 24.4V19.2', 1.1, P.darkGreen),
    flat(
      P.purple,
      ellipse(16, 18.6, 0.9, 1.2) +
        ellipse(15, 20.4, 0.9, 1.1) +
        ellipse(17, 20.4, 0.9, 1.1) +
        ellipse(15.1, 22.2, 0.8, 1) +
        ellipse(16.9, 22.2, 0.8, 1),
    ),
    shine(rect(11.6, 13.8, 1.2, 2.6, 0.6), 0.45),
  ],
  /** A dented metal garbage can with its lid. */
  'trash-can': [
    fill(
      P.lightGray,
      'M6.4 10.4H25.6L24.9 16.2L23.1 18.2L24.3 20.4L23.6 26.4C23.5 27.2 22.8 27.8 22 27.8H10C9.2 27.8 8.5 27.2 8.4 26.4Z',
    ),
    line('M11.4 11.6V26.4M16 11.6V26.6M20.6 11.6V16.4M20.6 21V26.4', 1.1, P.gray),
    line('M23.1 18.2C21.6 18.4 20.6 19 19.8 19.8', 1.2),
    fill(P.silver, turn('M5 10.4C5 8.4 9.6 7 16 7C22.4 7 27 8.4 27 10.4V11.2H5Z', -5, 16, 9)),
    line(turn('M13.4 7.6C13.4 4.6 18.6 4.6 18.6 7.6', -5, 16, 9), 1.6),
  ],
  /** A shopping cart. */
  cart: [
    line('M4.6 6.6H6.6L9.8 21.6H25.4', 1.8),
    fill(P.silver, poly(6.8, 9, 28.4, 9, 26.2, 18.4, 8.8, 18.4)),
    line('M12 10.4V17M16.6 10.4V17M21.2 10.4V17M9.2 13.6H26', 1.1, P.gray),
    fill(P.red, rect(2.4, 4.8, 4.6, 3, 1.4)),
    fill(P.black, circle(11.4, 25.4, 2.2) + circle(23.4, 25.4, 2.2)),
  ],
  /** A brown paper grocery sack, a loaf and some celery poking out. */
  'grocery-bag': [
    fill(P.bun, turn(rect(7, 2.6, 5.4, 16, 2.7), -20, 9.7, 12)),
    line(turn('M8.4 5.8L11 5M8.4 8.6L11 7.8M8.4 11.4L11 10.6', -20, 9.7, 12), 1.2, P.wood),
    fill(P.lime, 'M18.6 14L19.2 6.2H21.4L21.2 14ZM21.4 14L23.2 7.4L25 7.8L23.8 14Z', { w: 1.3 }),
    fill(
      P.green,
      'M17.6 6.6C17 4.4 18.6 3 20.2 3.8C21 2.4 23.2 2.6 23.6 4.2C25.4 4 26.6 5.8 25.6 7.4C24.4 8.6 22.6 8 21.6 7.4C20.4 8.4 18.4 8.2 17.6 6.6Z',
      { w: 1.3 },
    ),
    fill(
      P.tan,
      'M5.4 12.4L7.6 13.8L9.8 12.4L12 13.8L14.2 12.4L16.4 13.8L18.6 12.4L20.8 13.8L23 12.4L25.2 13.8L26.6 12.6L27.2 28.6H4.8Z',
    ),
    flat(P.wood, 'M6.2 15.6H25.8L25.9 17.2H6.1Z', 0.4),
  ],
  /** A wrapped present with a ribbon bow. */
  gift: moved(0, -0.6, [
    fill(
      P.gold,
      'M16 9.8C13.6 6.6 10.6 4.6 8.8 5.8C7 7 8.8 9.8 16 9.8Z' +
        mirror('M16 9.8C13.6 6.6 10.6 4.6 8.8 5.8C7 7 8.8 9.8 16 9.8Z'),
    ),
    fill(P.red, rect(5.4, 14, 21.2, 14.4, 1)),
    fill(P.gold, rect(14.2, 14, 3.6, 14.4)),
    fill(P.red, rect(3.8, 9.8, 24.4, 5, 1)),
    fill(P.gold, rect(14.2, 9.8, 3.6, 5)),
    fill(P.gold, rect(14, 7.8, 4, 3, 1.2)),
  ]),
  /** A closed cardboard box, taped shut. */
  box: moved(0, -0.8, [
    fill(P.beige, poly(4, 12.4, 12, 6.4, 28, 6.4, 20, 12.4)),
    fill(P.wood, poly(20, 12.4, 28, 6.4, 28, 21.6, 20, 27.6)),
    fill(P.tan, rect(4, 12.4, 16, 15.2)),
    flat(P.white, poly(11.77, 11.6, 14.37, 11.6, 20.23, 7.2, 17.63, 7.2) + rect(10.7, 13.2, 2.6, 4), 0.5),
  ]),
  /** A ball of red yarn with a loose strand. */
  yarn: moved(-0.8, -0.4, [
    ...cord('M21.8 22.4C24.2 24.2 26.6 23.4 28 25.4C29 27 27.6 28.6 25.8 28', 1.4, P.red),
    fill(P.red, circle(15, 14.6, 10.6)),
    line(
      'M9.6 7.6C14.6 9.6 19.2 16 18.8 23.6M7 10.8C11.6 12.6 15.6 18.2 15 24.2M6 15.8C9.4 17 11.8 20 11.6 22.6',
      1.3,
      P.darkRed,
    ),
    line('M13.4 5.4C18.6 6.6 22.6 10.6 24 15.6M18 5.8C21 7.2 23.2 9.6 24.2 12.4', 1.3, P.darkRed),
  ]),
  /** A pair of scissors, open. */
  scissors: moved(0, -1, [
    ...turned(-24, 16.4, 15, [
      fill(P.silver, turn(SCISSOR_HALF, 17, 16, 16)),
      ...cord(turn(ellipse(6.6, 16, 2.9, 2.6), 17, 16, 16), 2, P.red),
      fill(P.silver, turn(SCISSOR_HALF, -17, 16, 16)),
      ...cord(turn(ellipse(6.6, 16, 2.9, 2.6), -17, 16, 16), 2, P.red),
      fill(P.gray, circle(16, 16, 1.4), { w: 1.1 }),
    ]),
  ]),
  /** A wood axe. */
  axe: [
    ...turned(38, 16, 18, [
      fill(P.wood, 'M14.6 8H17.4L17.8 29C17.8 30.2 14.2 30.2 14.2 29Z'),
      fill(
        P.gray,
        mirror(
          'M12.6 7.4H18.6C20.6 6.4 22.8 5.6 25 5.2C26.4 8.4 26.4 12.8 25 16.2C22.8 15.8 20.6 15 18.6 13.8H12.6Z',
        ),
      ),
      flat(P.silver, mirror('M23.6 6.4C24.8 9 24.8 12.6 23.6 15.2L24.4 15.4C25.4 12.6 25.4 8.8 24.4 6Z')),
    ]),
  ],
  /** A claw hammer. */
  hammer: moved(1.2, -0.8, [
    ...turned(-40, 16, 16, [
      fill(P.wood, rect(14.4, 12.6, 3.2, 18, 1.4)),
      fill(P.black, rect(14, 22.6, 4, 8.2, 1.6)),
      fill(
        P.gray,
        'M6.4 8.2C6.4 7.4 7 6.8 7.8 6.8H10V8H18.6C21.6 8 24.6 9.2 26.8 12.4C24.6 11.8 21.8 11.6 19.4 12.4V14H10V15.2H7.8C7 15.2 6.4 14.6 6.4 13.8Z',
      ),
    ]),
  ]),
  /** A silver wrench. */
  wrench: [
    ...turned(45, 16, 16, [
      fill(
        P.silver,
        'M14 2.42A5 5 0 0 0 14.2 11.67L14.2 20.98A4.4 4.4 0 1 0 17.8 20.98L17.8 11.67A5 5 0 0 0 18 2.42L18 6.4Q18 7.4 17 7.4H15Q14 7.4 14 6.4Z' +
          hole(16, 25, 1.9),
      ),
      shine(rect(15.2, 12.4, 1, 8, 0.5), 0.6),
    ]),
  ],
  /** A rusty bolt and nut: scrap metal. */
  scrap: moved(0.6, 0.6, [
    ...turned(-40, 12.4, 12.4, [
      fill(P.steel, rect(7.4, 3.4, 10, 5.6, 1)),
      line('M10.8 4.8V7.6M14.2 4.8V7.6', 1.1, P.darkGray),
      fill(P.steel, rect(9.2, 8.8, 6.4, 12.8, 0.8)),
      line('M10.2 11L14.6 10.2M10.2 13.6L14.6 12.8M10.2 16.2L14.6 15.4M10.2 18.8L14.6 18', 1.2, P.darkGray),
      flat(P.rust, 'M8.2 4.2H10.4L10 6.2L8.2 7.4Z'),
      flat(P.rust, 'M14.8 19C14.8 17.8 13.4 17.6 13 18.6L12.4 20.8H14.8Z'),
    ]),
    fill(
      P.rust,
      poly(22, 15.6, 27.2, 18.6, 27.2, 24.6, 22, 27.6, 16.8, 24.6, 16.8, 18.6) + circle(22, 21.6, 2.2),
    ),
    flat(P.darkBrown, circle(19.2, 23.8, 0.9) + circle(25, 19.4, 0.8), 0.6),
  ]),
  /** A gray metal cog (car parts). */
  gear: [
    fill(P.gray, cog(16, 16, 8, 9.8, 12.8) + circle(16, 16, 3.4)),
    line(circle(16, 16, 6.4), 1.2, P.darkGray),
    shine(ellipse(11.4, 9.6, 1.8, 1), 0.5),
  ],
  /** A coil of shiny copper wire. */
  'copper-wire': [
    ...cord(ellipse(14.6, 13.6, 10.6, 8), 1.8, COPPER, 1.1),
    ...cord(ellipse(16, 15.2, 10.6, 8), 1.8, COPPER, 1.1),
    ...cord(ellipse(17.4, 16.8, 10.6, 8), 1.8, COPPER, 1.1),
    ...cord('M27.4 19.4C28.8 22.2 27.6 25.8 24.6 27.6', 1.8, COPPER, 1.1),
    line('M8.4 10.6C10 8.8 12.4 7.6 14.8 7.4M9.6 12.4C11.4 10.6 13.8 9.4 16.2 9.2', 0.9, P.white, 0.75),
  ],
  /** A battery, full: all green. */
  battery: [
    fill(P.gray, rect(25.4, 12.6, 3.6, 6.8, 1.2)),
    fill(P.silver, rect(3, 8.6, 23.4, 14.8, 3)),
    fill(P.lime, rect(5.6, 11.2, 18.2, 9.6, 1.6), { w: 1.2 }),
    shine(rect(7, 12.4, 15.4, 1.8, 0.9), 0.5),
  ],
  /** A battery nearly flat: a sliver of red. */
  'battery-low': [
    fill(P.gray, rect(25.4, 12.6, 3.6, 6.8, 1.2)),
    fill(P.silver, rect(3, 8.6, 23.4, 14.8, 3)),
    fill(P.charcoal, rect(5.6, 11.2, 18.2, 9.6, 1.6), { w: 1.2 }),
    flat(P.red, rect(6.4, 12, 3.4, 8, 0.8)),
  ],
  /** A black Mag-Lite flashlight throwing a yellow beam. */
  flashlight: moved(0, 1.2, [
    ...turned(-28, 16, 17, [
      flat(P.yellow, poly(19.6, 12.2, 27.6, 9, 27.6, 23, 19.6, 19.8), 0.6),
      fill(P.charcoal, rect(3, 13.4, 12, 5.2, 1.6)),
      line('M5.2 14.6V17.4M7 14.6V17.4M8.8 14.6V17.4', 1, P.darkGray),
      fill(P.darkGray, rect(10.6, 11.8, 2.6, 1.8, 0.6), { w: 1 }),
      fill(P.charcoal, 'M14.4 13.2L18 11V21L14.4 18.8Z'),
      fill(P.lemon, rect(17.6, 10.6, 2.2, 10.8, 1), { w: 1.2 }),
    ]),
  ]),
  /** A headlamp: an elastic strap, a bright lamp out front, light rays. */
  headlamp: [
    ...cord('M4.2 13C4.2 9.2 9.4 6.6 16 6.6C22.6 6.6 27.8 9.2 27.8 13', 2, P.navy, 1.1),
    ...cord('M4.2 13C4.2 16.6 9.4 19.4 16 19.4C22.6 19.4 27.8 16.6 27.8 13', 2, P.blue, 1.1),
    line(rays(16, 17.3, 7.4, 10.4, [20, 55, 90, 125, 160]), 1.6, P.amber),
    fill(P.charcoal, rect(9.8, 12.4, 12.4, 9.8, 3.4)),
    fill(P.lemon, circle(16, 17.3, 3.6)),
    shine(circle(14.8, 16.1, 1.2), 0.85),
  ],
  /** A white satellite dish on a short pole. */
  'satellite-dish': [
    fill(P.gray, rect(16.2, 17.6, 2.8, 8.4)),
    fill(P.darkGray, rect(11.6, 25.4, 12, 3, 1)),
    fill(P.lightGray, turn(ellipse(14.2, 15, 9.4, 5.6), 45, 14.2, 15)),
    fill(P.white, turn(ellipse(15.8, 13.4, 9.6, 4.2), 45, 15.8, 13.4)),
    line('M15.8 13.4L22.8 6.4', 1.5),
    fill(P.gray, turn(rect(21.6, 4.2, 3.2, 4.4, 1), 45, 23.2, 6.4), { w: 1.2 }),
  ],
  /** A crinkly tinfoil hat: a crumpled silver cone on a wide brim, catching the light. */
  'foil-hat': turned(-6, 16, 24, [
    fill(
      P.silver,
      poly(
        17.6,
        2.6,
        19.6,
        7.8,
        21.4,
        12.4,
        23.2,
        17,
        24.4,
        21.6,
        16.2,
        23.4,
        7.8,
        21.8,
        9.6,
        16.6,
        11.8,
        11.6,
        14.6,
        6.8,
      ),
    ),
    flat(P.white, poly(17.2, 4.6, 16.4, 12.4, 12.8, 14.4, 14.8, 8.6), 0.85),
    flat(P.gray, poly(17.8, 12.6, 21.8, 13.8, 23, 19.2, 18.6, 20.2), 0.45),
    line('M17.6 3.8L16.4 12.4L18.4 20M16.4 12.4L11.6 14.8M16.4 12.4L21.4 13.6', 1, P.gray),
    fill(
      P.silver,
      poly(
        3,
        22.2,
        6.6,
        19.8,
        11,
        21,
        16.2,
        20.2,
        21.4,
        20.8,
        25.6,
        19.6,
        29,
        21.8,
        27.4,
        25,
        22.6,
        26.8,
        16.4,
        27.6,
        10,
        27,
        5,
        25.4,
      ),
    ),
    line('M6.2 23.4L10.4 24.6L14.2 23.6L18.6 24.8L22.6 23.4L26.2 23.8', 1, P.gray),
    fill(P.white, sparkle(12.6, 9.6, 2.1), { w: 0.9 }),
    fill(P.white, sparkle(21.6, 17.4, 1.5), { w: 0.9 }),
  ]),
  /** An oil-drum smoker on a stand, smoke curling out of the stack. */
  smoker: [
    line('M24.4 7.2C22.6 5.8 25.6 4.6 23.8 3M21.4 7.6C20 6.4 22.4 5.2 21 3.8', 1.6, P.gray),
    line('M7.4 21.8L6.4 28.2M24.6 21.8L25.6 28.2M6.8 25.4H25.2', 1.8),
    fill(P.charcoal, rect(22.4, 8, 3.6, 7.4, 0.6)),
    fill(P.charcoal, rect(3.4, 12.6, 25.2, 10.6, 5.3)),
    line('M5.2 17.6H26.8', 1.3, P.gray),
    fill(P.wood, rect(12.4, 10.2, 7.2, 2, 1), { w: 1.2 }),
    line('M13.8 12.6V11.6M18.2 12.6V11.6', 1.2),
    shine(rect(7, 14, 4, 1.4, 0.7), 0.35),
  ],
  /** A round blue plastic kiddie pool full of water, with a rubber duck. */
  'kiddie-pool': moved(0, -1.8, [
    fill(P.blue, 'M3 16.6V20.2C3 24 8.8 26.6 16 26.6C23.2 26.6 29 24 29 20.2V16.6Z'),
    fill(P.blue, ellipse(16, 16.6, 13, 6.4)),
    flat(P.water, ellipse(16, 16.8, 10.6, 4.4)),
    line('M8.6 17.6C10.2 16.8 11.8 18.4 13.4 17.6', 1.2, P.white, 0.8),
    flat(
      P.white,
      circle(7, 22.4, 0.9) + circle(12, 24, 0.9) + circle(18, 24.4, 0.9) + circle(24, 22.8, 0.9),
      0.85,
    ),
    fill(P.yellow, ellipse(20, 16.6, 3, 1.8), { w: 1.2 }),
    fill(P.yellow, circle(21.8, 13.8, 1.7), { w: 1.2 }),
    fill(P.orange, poly(23.2, 13.4, 25, 14, 23.2, 14.6), { w: 0.9 }),
  ]),
  /** An above-ground pool: a round blue wall, a ladder, water on top. */
  'above-ground-pool': moved(0, -1.2, [
    fill(P.blue, 'M3.4 12V24.6C3.4 26.6 9 28 16 28C23 28 28.6 26.6 28.6 24.6V12Z'),
    flat(P.sky, rect(7, 13.4, 1.6, 12.6) + rect(13, 13.4, 1.6, 13.6), 0.7),
    fill(P.white, ellipse(16, 12, 12.6, 3.6)),
    flat(P.water, ellipse(16, 12.2, 11, 2.4)),
    ...cord(
      'M21.6 27.6V9.4C21.6 8 23 7.4 23.8 8.4M26 27.6V9.4C26 8 27.4 7.4 28.2 8.4M21.6 13.6H26M21.6 18.2H26M21.6 22.8H26',
      1.2,
      P.white,
    ),
  ]),
  /** A rusty backyard trampoline, side on. */
  trampoline: [
    ...cord('M5.2 14.4V24H10.4V17.6M26.8 14.4V24H21.6V17.6M13.6 18.8V24.6H18.4V18.8', 1.3, P.rust, 0.75),
    fill(P.rust, ellipse(16, 13.4, 13.2, 5.6)),
    flat(P.silver, ellipse(16, 13.4, 11.6, 4.2)),
    line(
      Array.from({ length: 20 }, (_, j) => {
        const a = (j * Math.PI) / 10;
        const c = Math.cos(a);
        const s = Math.sin(a);
        return `M${r2(16 + 9.8 * c)} ${r2(13.4 + 3.2 * s)}L${r2(16 + 11.4 * c)} ${r2(13.4 + 4.3 * s)}`;
      }).join(''),
      0.9,
      P.gray,
    ),
    flat(P.charcoal, ellipse(16, 13.4, 9.8, 3.2)),
  ],
  /** A bug zapper lantern glowing purple-blue, a little zap spark off its side. */
  'bug-zapper': moved(0, 0.8, [
    flat(P.purple, circle(16, 16.4, 11.4), 0.22),
    line(circle(16, 4.1, 1.4), 1.4),
    fill(P.charcoal, 'M9.4 9.8C9.4 7.4 12.4 5.4 16 5.4C19.6 5.4 22.6 7.4 22.6 9.8Z'),
    fill(P.lavender, rect(10.6, 9.6, 10.8, 13, 0.6)),
    flat(P.white, rect(14.6, 10.4, 2.8, 11.4, 1.4), 0.8),
    line('M13 10.6V21.6M16 10.6V21.6M19 10.6V21.6', 1.1, P.charcoal),
    fill(P.charcoal, rect(9, 22.2, 14, 3, 1.2)),
    fill(P.yellow, 'M24.4 7.6L22.4 11.6H24.6L23 15.2L27 10.4H24.8L26.2 7.6Z', { w: 1.1 }),
  ]),
  /** A string of Christmas lights: red, green, yellow and blue bulbs on a wire. */
  'xmas-lights': [
    line(
      'M2.8 7.4C3.8 8.6 4.6 9.4 5.6 9.6C8.4 10.2 10 12 12.2 12.2C14.6 12.4 16.4 10.2 18.8 10C21.2 9.8 23 12.2 25.4 12.4C26.8 12.5 28.2 11.6 29.2 10.4',
      1.5,
      P.darkGreen,
    ),
    ...bulb(5.6, 9.6, 10, P.red),
    ...bulb(12.2, 12.2, -4, P.green),
    ...bulb(18.8, 10, 6, P.yellow),
    ...bulb(25.4, 12.4, -8, P.blue),
  ],
  /** A white wooden sign that says Live Laugh Love in curly script. */
  'lll-sign': moved(0, 1.6, [
    line(polyline(5.6, 8.4, 16, 3.2, 26.4, 8.4), 1.2, P.wood),
    fill(P.gray, circle(16, 3.2, 1.2), { w: 1.1 }),
    fill(P.white, rect(2.8, 7.6, 26.4, 17.6, 1.6)),
    line(scrawl(6.4, 12.8, [3, 1.4, 1.4, 1.4]), 1.2),
    line(scrawl(10.4, 17.4, [3, 1.4, 1.4, 3, 1.4]), 1.2),
    line(scrawl(6.4, 22, [3, 1.4, 1.4, 1.4]), 1.2),
    fill(
      P.red,
      'M21 19.6C20 18.4 18.6 18.8 18.6 20C18.6 21.2 20 22 21 23C22 22 23.4 21.2 23.4 20C23.4 18.8 22 18.4 21 19.6Z',
      { w: 1 },
    ),
  ]),
  /** A yard sign on a wooden stake: blank cream with a red border. */
  'yard-sign': [
    fill(P.wood, poly(14.6, 17, 17.4, 17, 17.4, 26.6, 16, 28.6, 14.6, 26.6)),
    fill(
      P.grass,
      'M10 29.4C10.4 27.4 11.4 26.4 12.4 26.2L13 27.4L14 25.6L15.6 27.2L16.6 25.4L18 27.2L19.4 25.8L20.2 27.4L21 26.4C22 26.8 22.4 28 22.6 29.4Z',
      { w: 1.2 },
    ),
    fill(P.red, rect(3.6, 4.4, 24.8, 14.6, 1.4)),
    flat(P.cream, rect(6, 6.8, 20, 9.8, 0.6)),
  ],
  /** A clothesline between two poles: a pair of tighty-whities and a sock pinned on it. */
  clothesline: moved(0, -1, [
    fill(P.wood, rect(3, 5.6, 2.2, 23.2, 0.6) + rect(26.8, 5.6, 2.2, 23.2, 0.6)),
    line('M4.2 7.8C10 11.2 22 11.2 27.8 7.8', 1.1),
    fill(P.white, 'M6 9.8L16 11.2L15.9 13.6C14.2 14.2 12.8 16.6 12.6 19.6H10.6C10.6 16.6 9.2 13.8 6.4 12.8Z'),
    line('M7.6 12.3L14.8 13.3', 1.3, P.blue),
    fill(
      P.white,
      'M18.6 11.2L22.8 10.8L23.2 18.4C23.4 19.6 24.6 20.2 25.8 20.4C26.6 21.6 26 23.6 24.4 23.8C21.4 24 19.4 22.4 19.2 19.8Z',
    ),
    flat(P.red, 'M19.6 12.6L22.2 12.4L22.25 13.7L19.65 13.9Z'),
    flat(P.blue, 'M19.7 14.6L22.3 14.4L22.35 15.5L19.75 15.7Z'),
    fill(
      P.wood,
      rect(7.2, 8.4, 1.4, 3.2, 0.4) + rect(14, 9.4, 1.4, 3.2, 0.4) + rect(20.2, 9.4, 1.4, 3.2, 0.4),
      { w: 1 },
    ),
  ]),
} satisfies Record<string, IconDef>;
